import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertCircle, ChevronDown, ChevronRight, Crosshair, Download, HelpCircle, Layers, LayoutList, Search, TrendingUp, Users, X } from 'lucide-react';
import {
  Button, Card, CountrySelect, EmptyState, OperatorBadge, OperatorSelect, PageSpinner, ProductIntro,
  TBody, TD, TH, THead, TR, Table,
} from '@/components/ui';
import { api } from '@/lib/api/client';
import { isApiError, unwrap } from '@/lib/api/envelope';
import { Endpoints } from '@/lib/api/endpoints';
import { downloadCsv } from '@/lib/utils/csvExport';
import type { ResponseEnvelope } from '@/types/api';

// ── Types ────────────────────────────────────────────────────────────────────

interface OpportunityGroup {
  mobileNumber: string;
  metadataId: string | null;
  operator: string | null;
  countryCode: string | null;
  totalCount: number;
  lastSeenAt: string | null;
  avgLatencyMs: number | null;
}

interface GroupedPage {
  content: OpportunityGroup[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
}

interface OpportunityRecord {
  id: string;
  mobileNumber: string;
  operator: string | null;
  countryCode: string | null;
  latencyMs: number | null;
  metadataId: string | null;
  createdAt: string;
}

interface CampaignSummary {
  totalIdentified: number;
  identifiedToday: number;
  identifiedThisMonth: number;
  byOperator: Record<string, number>;
  byCountry: Record<string, number>;
  recent: OpportunityRecord[];
  totalRecords: number;
  page: number;
  pageSize: number;
}

interface OpportunityStats {
  totalIdentified: number;
  totalUniqueIdentified: number;
  totalGroups: number;
  totalCost: number;
}

interface Filters {
  mobile: string;
  operator: string;
  countryCode: string;
  from: string;
  to: string;
}

type ViewMode = 'grouped' | 'flat';

interface StuckPlusOverview {
  stuckCount: number;
  stuckPlusCount: number;
  avgAttemptsBeforeRecovery: number;
  totalSessions: number;
}

// ── API helpers ───────────────────────────────────────────────────────────────

function toInstant(date: string, endOfDay = false): string {
  if (!date) return '';
  if (date.includes('T')) return date;
  return `${date}T${endOfDay ? '23:59:59' : '00:00:00'}Z`;
}

function buildParams(filters: Filters, page: number, size: number): Record<string, unknown> {
  const p: Record<string, unknown> = { page, size };
  if (filters.mobile) p.mobile = filters.mobile;
  if (filters.operator) p.operator = filters.operator;
  if (filters.countryCode) p.countryCode = filters.countryCode;
  const from = toInstant(filters.from);
  const to = toInstant(filters.to, true);
  if (from) p.from = from;
  if (to) p.to = to;
  return p;
}

async function fetchOpportunityStats(filters: Filters): Promise<OpportunityStats> {
  const p: Record<string, unknown> = {};
  if (filters.mobile) p.mobile = filters.mobile;
  if (filters.operator) p.operator = filters.operator;
  if (filters.countryCode) p.countryCode = filters.countryCode;
  const from = toInstant(filters.from);
  const to = toInstant(filters.to, true);
  if (from) p.from = from;
  if (to) p.to = to;
  const res = await api.get<ResponseEnvelope<OpportunityStats>>(Endpoints.me.opportunityStats, { params: p });
  return unwrap(res);
}

async function fetchStats(filters: Filters, page: number): Promise<CampaignSummary> {
  const res = await api.get<ResponseEnvelope<CampaignSummary>>(Endpoints.me.opportunities, {
    params: { ...buildParams(filters, page, 25), maskMobile: false },
  });
  return unwrap(res);
}

async function fetchGrouped(filters: Filters, page: number): Promise<GroupedPage> {
  const res = await api.get<ResponseEnvelope<GroupedPage>>(Endpoints.me.opportunitiesGrouped, {
    params: buildParams(filters, page, 25),
  });
  return unwrap(res);
}

async function fetchDetail(mobile: string): Promise<OpportunityRecord[]> {
  const res = await api.get<ResponseEnvelope<CampaignSummary>>(Endpoints.me.opportunities, {
    params: { mobile, page: 0, size: 25, maskMobile: false },
  });
  return unwrap(res).recent;
}

// ── Sub-components ────────────────────────────────────────────────────────────

function ExpandedDetail({ mobile }: { mobile: string }) {
  const q = useQuery({
    queryKey: ['me', 'opportunities', 'detail', mobile],
    queryFn: () => fetchDetail(mobile),
    staleTime: 60_000,
  });

  if (q.isLoading) {
    return (
      <tr>
        <td colSpan={8} className="py-4 text-center text-sm text-fg-muted">
          Loading records…
        </td>
      </tr>
    );
  }
  if (q.isError) {
    return (
      <tr>
        <td colSpan={8} className="py-4 text-center text-sm text-rose-500">
          Failed to load records.
        </td>
      </tr>
    );
  }

  const rows = q.data ?? [];
  if (rows.length === 0) {
    return (
      <tr>
        <td colSpan={8} className="py-4 text-center text-sm text-fg-muted">
          No individual records found for this visitor.
        </td>
      </tr>
    );
  }

  return (
    <>
      {rows.map((r) => (
        <tr key={r.id} className="bg-surface-muted/40 text-xs border-t border-border/40">
          <td className="w-8 pl-3 pr-0" />
          <td className="px-3 py-2 font-mono text-[11px] text-fg-subtle" colSpan={2}>
            {r.id}
          </td>
          <td className="px-3 py-2">
            <OperatorBadge operator={r.operator} />
          </td>
          <td className="px-3 py-2 text-fg-muted">{r.countryCode ?? '—'}</td>
          <td className="px-3 py-2 text-fg-muted">—</td>
          <td className="px-3 py-2 text-fg-muted">
            {r.latencyMs != null ? `${r.latencyMs} ms` : '—'}
          </td>
          <td className="px-3 py-2 text-fg-muted">
            {r.createdAt ? new Date(r.createdAt).toLocaleString() : '—'}
          </td>
        </tr>
      ))}
    </>
  );
}

function BarChart({ data, label }: { data: Record<string, number>; label: string }) {
  const entries = Object.entries(data)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8);
  if (entries.length === 0) return null;
  const total = entries.reduce((s, [, v]) => s + v, 0);
  const max = entries[0]![1];

  return (
    <div>
      <p className="mb-3 text-xs font-semibold uppercase tracking-widest text-fg-subtle">{label}</p>
      <div className="space-y-3">
        {entries.map(([key, count]) => {
          const pct = total === 0 ? 0 : (count / total) * 100;
          return (
            <div key={key}>
              <div className="flex items-center justify-between text-xs">
                <span className="font-medium text-fg">{key}</span>
                <span className="text-fg-muted">
                  {count.toLocaleString()}
                  <span className="ml-1.5 text-[10px] text-fg-subtle">{pct.toFixed(1)}%</span>
                </span>
              </div>
              <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-surface-subtle">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${max > 0 ? (count / max) * 100 : 0}%`,
                    background: 'linear-gradient(90deg, #60a5fa, #2563eb)',
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function GroupedTable({
  groups,
  expandedRow,
  onToggleRow,
}: {
  groups: OpportunityGroup[];
  expandedRow: string | null;
  onToggleRow: (key: string) => void;
}) {
  if (groups.length === 0) {
    return (
      <EmptyState message="No visitor identification records yet. Records appear here once your integration starts capturing web visitors via the Authmatech SDK." />
    );
  }

  return (
    <Table>
      <THead>
        <TR>
          <TH className="w-8" />
          <TH>Mobile Number</TH>
          <TH>Metadata ID</TH>
          <TH>Operator</TH>
          <TH>Country</TH>
          <TH>Visits</TH>
          <TH>Avg Latency</TH>
          <TH>Last Seen</TH>
        </TR>
      </THead>
      <TBody>
        {groups.map((g) => {
          const rowKey = `${g.mobileNumber}::${g.metadataId ?? ''}`;
          const isExpanded = expandedRow === rowKey;
          return (
            <Fragment key={rowKey}>
              <TR
                className="cursor-pointer transition-colors hover:bg-surface-muted/50"
                onClick={() => onToggleRow(rowKey)}
              >
                <TD className="w-8 pl-3 pr-0">
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4 text-fg-muted" />
                  ) : (
                    <ChevronRight className="h-4 w-4 text-fg-muted" />
                  )}
                </TD>
                <TD className="font-mono text-xs">{g.mobileNumber || '—'}</TD>
                <TD className="font-mono text-[11px] text-fg-subtle">
                  {g.metadataId || '—'}
                </TD>
                <TD>
                  <OperatorBadge operator={g.operator} />
                </TD>
                <TD className="text-xs text-fg-muted">{g.countryCode ?? '—'}</TD>
                <TD className="text-xs font-semibold text-fg">
                  {g.totalCount.toLocaleString()}
                </TD>
                <TD className="text-xs text-fg-muted">
                  {g.avgLatencyMs != null ? `${g.avgLatencyMs} ms` : '—'}
                </TD>
                <TD className="text-[11px] text-fg-muted">
                  {g.lastSeenAt ? new Date(g.lastSeenAt).toLocaleString() : '—'}
                </TD>
              </TR>
              {isExpanded && <ExpandedDetail mobile={g.mobileNumber} />}
            </Fragment>
          );
        })}
      </TBody>
    </Table>
  );
}

function FlatTable({ rows }: { rows: OpportunityRecord[] }) {
  if (rows.length === 0) {
    return (
      <EmptyState message="No visitor identification records yet. Records appear here once your integration starts capturing web visitors via the Authmatech SDK." />
    );
  }

  return (
    <Table>
      <THead>
        <TR>
          <TH>Record ID</TH>
          <TH>Mobile</TH>
          <TH>Operator</TH>
          <TH>Country</TH>
          <TH>Latency</TH>
          <TH>Timestamp</TH>
        </TR>
      </THead>
      <TBody>
        {rows.map((row) => (
          <TR key={row.id}>
            <TD className="font-mono text-[11px] text-fg-subtle">{row.id}</TD>
            <TD className="font-mono text-xs">{row.mobileNumber}</TD>
            <TD>
              <OperatorBadge operator={row.operator} />
            </TD>
            <TD className="text-xs text-fg-muted">{row.countryCode ?? '—'}</TD>
            <TD className="text-xs text-fg-muted">
              {row.latencyMs != null ? `${row.latencyMs} ms` : '—'}
            </TD>
            <TD className="text-[11px] text-fg-muted">
              {new Date(row.createdAt).toLocaleString()}
            </TD>
          </TR>
        ))}
      </TBody>
    </Table>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function ClientOpportunityRequestsPage() {
  const [viewMode, setViewMode] = useState<ViewMode>('grouped');
  const [filters, setFilters] = useState<Filters>({
    mobile: '', operator: '', countryCode: '', from: '', to: '',
  });
  const [appliedFilters, setAppliedFilters] = useState<Filters>({
    mobile: '', operator: '', countryCode: '', from: '', to: '',
  });
  const [page, setPage] = useState(0);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const statsQuery = useQuery({
    queryKey: ['me', 'campaign-stats', appliedFilters, page],
    queryFn: () => fetchStats(appliedFilters, page),
    staleTime: 0,
  });

  const opportunityStatsQuery = useQuery({
    queryKey: ['me', 'opportunity-stats', appliedFilters],
    queryFn: () => fetchOpportunityStats(appliedFilters),
    staleTime: 0,
  });

  const groupedQuery = useQuery({
    queryKey: ['me', 'opportunities-grouped', appliedFilters, page],
    queryFn: () => fetchGrouped(appliedFilters, page),
    enabled: viewMode === 'grouped',
    staleTime: 0,
  });

  const stuckOverviewQuery = useQuery<StuckPlusOverview>({
    queryKey: ['stuckplus-overview'],
    queryFn: async () => unwrap(await api.get<ResponseEnvelope<StuckPlusOverview>>(Endpoints.me.stuckPlus.overview)),
    staleTime: 60_000,
    retry: 0,
  });

  const stats = statsQuery.data;
  const opportunityStats = opportunityStatsQuery.data;
  const stuckOv = stuckOverviewQuery.data;
  const grouped = groupedQuery.data;

  const hasActiveFilters = Object.values(appliedFilters).some(Boolean);

  const totalPages =
    viewMode === 'grouped'
      ? (grouped?.totalPages ?? 1)
      : stats
        ? Math.ceil(stats.totalRecords / Math.max(stats.pageSize, 1))
        : 1;

  const isLoading = viewMode === 'grouped' ? groupedQuery.isLoading : statsQuery.isLoading;
  const isError = viewMode === 'grouped' ? groupedQuery.isError : statsQuery.isError;
  const queryError = viewMode === 'grouped' ? groupedQuery.error : statsQuery.error;

  function applyFilters() {
    setAppliedFilters({ ...filters });
    setPage(0);
    setExpandedRow(null);
  }

  function resetFilters() {
    const empty: Filters = { mobile: '', operator: '', countryCode: '', from: '', to: '' };
    setFilters(empty);
    setAppliedFilters(empty);
    setPage(0);
    setExpandedRow(null);
  }

  function switchView(mode: ViewMode) {
    setViewMode(mode);
    setPage(0);
    setExpandedRow(null);
  }

  async function handleExport() {
    setIsExporting(true);
    try {
      const exportData = await fetchStats(appliedFilters, 0);
      const headers = ['ID', 'Mobile', 'Operator', 'Country', 'Latency (ms)', 'Metadata ID', 'Timestamp'];
      const csvRows = exportData.recent.map((r) => [
        r.id,
        r.mobileNumber ?? '',
        r.operator ?? '',
        r.countryCode ?? '',
        r.latencyMs ?? '',
        r.metadataId ?? '',
        r.createdAt ? new Date(r.createdAt).toLocaleString() : '',
      ]);
      downloadCsv(`opportunities-${new Date().toISOString().slice(0, 10)}.csv`, headers, csvRows);
    } finally {
      setIsExporting(false);
    }
  }

  const showBarCharts =
    stats &&
    (Object.keys(stats.byOperator).length > 0 || Object.keys(stats.byCountry).length > 0);

  const totalJourneys = (stuckOv?.stuckCount ?? 0) + (stuckOv?.stuckPlusCount ?? 0);
  const recoveryRate = totalJourneys > 0
    ? (((stuckOv?.stuckCount ?? 0) / totalJourneys) * 100).toFixed(1)
    : null;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Crosshair className="h-5 w-5 text-violet-500" />
            <h1 className="text-xl font-semibold text-fg">Authmatech Target</h1>
          </div>
          <p className="mt-0.5 text-sm text-fg-muted">
            Mobile identity targeting intelligence — visitor identification, recovery audiences &amp; CRM targeting.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleExport}
            disabled={isExporting || (stats?.totalRecords ?? 0) === 0}
          >
            <Download className="mr-1.5 h-3.5 w-3.5" />
            {isExporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        <div className="flex overflow-hidden rounded-lg border border-border bg-surface">
          <button
            onClick={() => switchView('grouped')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
              viewMode === 'grouped'
                ? 'bg-primary-500 text-white'
                : 'text-fg-muted hover:text-fg'
            }`}
          >
            <Layers className="h-3.5 w-3.5" />
            Grouped
          </button>
          <button
            onClick={() => switchView('flat')}
            className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium transition-colors ${
              viewMode === 'flat'
                ? 'bg-primary-500 text-white'
                : 'text-fg-muted hover:text-fg'
            }`}
          >
            <LayoutList className="h-3.5 w-3.5" />
            All Records
          </button>
        </div>
        </div>
      </div>

      <ProductIntro
        icon={Crosshair}
        tone="target"
        eyebrow="Customer Journey Recovery · Target"
        title="Turn anonymous, high-intent visitors into reconnectable opportunities."
        points={['Visitor identification', 'Consent-first', 'Campaign attribution', 'CRM-ready']}
      >
        <p>
          <strong>Authmatech Target</strong> recovers a verified mobile identity for a returning, consented
          visitor — using the same mobile-network signals behind Verify — so the customer who browsed and left
          becomes someone you can reach again.
        </p>
        <p>
          Each identification becomes an <strong>Opportunity</strong> you can route to outreach, match to the
          campaign that drove it, or push to your CRM. It’s built for high-volume web traffic and is the lightest
          intelligence action in the suite — and because it works on recognised, consented visitors, consent and
          data-retention controls come first.
        </p>
      </ProductIntro>

      {/* KPI cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="card-accent-top card-accent-primary relative overflow-hidden rounded-xl border border-border bg-surface p-4 shadow-card dark:shadow-card-dark sm:p-5">
          <p className="text-xs font-medium text-fg-muted">Total Identified</p>
          <p className="mt-2 text-2xl font-bold tracking-tight text-fg">
            {stats ? stats.totalIdentified.toLocaleString() : '—'}
          </p>
        </div>
        <div className="card-accent-top relative overflow-hidden rounded-xl border border-border bg-surface p-4 shadow-card dark:shadow-card-dark sm:p-5"
             style={{ borderTopColor: '#8b5cf6', borderTopWidth: '3px' }}>
          <div className="flex items-center gap-1.5">
            <p className="text-xs font-medium text-fg-muted">Total Unique Identified</p>
            <div className="group relative">
              <HelpCircle className="h-3.5 w-3.5 text-fg-subtle cursor-help" />
              <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-2 w-56 -translate-x-1/2 rounded-lg border border-border bg-surface px-3 py-2 text-[11px] text-fg-muted shadow-lg opacity-0 transition-opacity group-hover:opacity-100">
                Unique mobile identities identified within the selected period.
              </div>
            </div>
          </div>
          <p className="mt-2 text-2xl font-bold tracking-tight text-fg">
            {opportunityStats ? opportunityStats.totalUniqueIdentified.toLocaleString() : '—'}
          </p>
        </div>
        <div className="card-accent-top card-accent-success relative overflow-hidden rounded-xl border border-border bg-surface p-4 shadow-card dark:shadow-card-dark sm:p-5">
          <p className="text-xs font-medium text-fg-muted">Identified Today</p>
          <p className="mt-2 text-2xl font-bold tracking-tight text-fg">
            {stats ? stats.identifiedToday.toLocaleString() : '—'}
          </p>
        </div>
        <div className="card-accent-top card-accent-warning relative overflow-hidden rounded-xl border border-border bg-surface p-4 shadow-card dark:shadow-card-dark sm:p-5">
          <p className="text-xs font-medium text-fg-muted">This Month</p>
          <p className="mt-2 text-2xl font-bold tracking-tight text-fg">
            {stats ? stats.identifiedThisMonth.toLocaleString() : '—'}
          </p>
        </div>
      </div>

      {/* Target Intelligence — recovery audience KPIs */}
      {stuckOv && (
        <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-4 dark:border-violet-500/20 dark:bg-violet-500/5">
          <div className="mb-3 flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-violet-500" />
            <p className="text-xs font-semibold uppercase tracking-widest text-violet-700 dark:text-violet-400">Recovery Audience Intelligence</p>
          </div>
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-violet-200/60 bg-white/70 p-3 dark:border-violet-500/15 dark:bg-surface/60">
              <div className="flex items-center gap-1.5">
                <Users className="h-3.5 w-3.5 text-emerald-500" />
                <p className="text-[11px] font-medium text-fg-muted">Recovered Users</p>
              </div>
              <p className="mt-1.5 text-xl font-bold tracking-tight text-fg">
                {stuckOv.stuckCount.toLocaleString()}
              </p>
              <p className="mt-0.5 text-[10px] text-fg-subtle">Successfully recovered identity journeys</p>
            </div>
            <div className="rounded-lg border border-violet-200/60 bg-white/70 p-3 dark:border-violet-500/15 dark:bg-surface/60">
              <div className="flex items-center gap-1.5">
                <AlertCircle className="h-3.5 w-3.5 text-amber-500" />
                <p className="text-[11px] font-medium text-fg-muted">Unresolved Journeys</p>
              </div>
              <p className="mt-1.5 text-xl font-bold tracking-tight text-fg">
                {stuckOv.stuckPlusCount.toLocaleString()}
              </p>
              <p className="mt-0.5 text-[10px] text-fg-subtle">Eligible for recovery audience targeting</p>
            </div>
            <div className="rounded-lg border border-violet-200/60 bg-white/70 p-3 dark:border-violet-500/15 dark:bg-surface/60">
              <div className="flex items-center gap-1.5">
                <Crosshair className="h-3.5 w-3.5 text-violet-500" />
                <p className="text-[11px] font-medium text-fg-muted">Recovery Conversion Rate</p>
              </div>
              <p className="mt-1.5 text-xl font-bold tracking-tight text-fg">
                {recoveryRate != null ? `${recoveryRate}%` : '—'}
              </p>
              <p className="mt-0.5 text-[10px] text-fg-subtle">Journeys resolved out of total identified</p>
            </div>
          </div>
        </div>
      )}

      {/* Filter bar */}
      <Card>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
          <div className="relative lg:col-span-2">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-fg-muted" />
            <input
              type="text"
              placeholder="Search mobile number…"
              value={filters.mobile}
              onChange={(e) => setFilters((f) => ({ ...f, mobile: e.target.value }))}
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              className="w-full rounded-lg border border-border bg-surface py-1.5 pl-8 pr-3 text-sm text-fg placeholder:text-fg-muted focus:outline-none focus:ring-2 focus:ring-primary-500/30"
            />
          </div>
          <CountrySelect
            value={filters.countryCode}
            onChange={(code) => {
              setFilters((f) => ({ ...f, countryCode: code, operator: '' }));
            }}
          />
          <OperatorSelect
            value={filters.operator}
            onChange={(code) => setFilters((f) => ({ ...f, operator: code }))}
            countryCode={filters.countryCode || undefined}
          />
          <div className="flex gap-2">
            <input
              type="date"
              value={filters.from}
              onChange={(e) => setFilters((f) => ({ ...f, from: e.target.value }))}
              className="min-w-0 flex-1 rounded-lg border border-border bg-surface px-2 py-1.5 text-xs text-fg focus:outline-none focus:ring-2 focus:ring-primary-500/30"
            />
            <input
              type="date"
              value={filters.to}
              onChange={(e) => setFilters((f) => ({ ...f, to: e.target.value }))}
              className="min-w-0 flex-1 rounded-lg border border-border bg-surface px-2 py-1.5 text-xs text-fg focus:outline-none focus:ring-2 focus:ring-primary-500/30"
            />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="flex items-center gap-1 text-xs text-fg-muted hover:text-fg"
            >
              <X className="h-3.5 w-3.5" />
              Clear
            </button>
          )}
          <Button size="sm" onClick={applyFilters}>
            Apply Filters
          </Button>
        </div>
      </Card>

      {/* Distribution bar charts */}
      {showBarCharts && (
        <div className="grid gap-4 sm:grid-cols-2">
          {Object.keys(stats.byOperator).length > 0 && (
            <Card>
              <BarChart data={stats.byOperator} label="By Operator" />
            </Card>
          )}
          {Object.keys(stats.byCountry).length > 0 && (
            <Card>
              <BarChart data={stats.byCountry} label="By Country" />
            </Card>
          )}
        </div>
      )}

      {/* Main table */}
      <Card noPadding>
        {isLoading ? (
          <div className="py-12">
            <PageSpinner />
          </div>
        ) : isError ? (
          <div className="px-5 py-8">
            <div
              role="alert"
              className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm dark:border-rose-500/25 dark:bg-rose-500/10"
            >
              <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
              <p className="text-rose-700 dark:text-rose-300">
                {isApiError(queryError)
                  ? queryError.firstErrorMessage
                  : 'Could not load opportunities.'}
              </p>
            </div>
          </div>
        ) : viewMode === 'grouped' ? (
          <GroupedTable
            groups={grouped?.content ?? []}
            expandedRow={expandedRow}
            onToggleRow={(key) => setExpandedRow((prev) => (prev === key ? null : key))}
          />
        ) : (
          <FlatTable rows={stats?.recent ?? []} />
        )}

        {/* Pagination */}
        {totalPages > 1 && !isLoading && !isError && (
          <div className="flex items-center justify-between border-t border-border px-4 py-3 sm:px-5">
            <span className="text-xs text-fg-muted">
              {viewMode === 'grouped'
                ? `${grouped?.totalElements?.toLocaleString() ?? 0} unique visitors`
                : `${stats?.totalRecords?.toLocaleString() ?? 0} records`}{' '}
              · Page {page + 1} of {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage((p) => p - 1)}
              >
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page + 1 >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
